'use client'

/**
 * ChatMessagesMinimap
 *
 * Minimal minimap for the chat messages list. Shows on scroll, animates away
 * on click/tap outside. Hover shows "You / Assistant" preview aligned to the
 * segment; click scrolls to that exact message so what you click matches
 * where it goes.
 *
 * Primary responsibilities:
 * - Show viewport indicator and message segments
 * - Hover: preview tooltip aligned to segment
 * - Click: scroll to actual message element (scrollIntoView) for accurate navigation
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { IconUser } from '@tabler/icons-react'
import logo from '@/public/logo.png'
import { cn } from '@/lib/utils'

const MIN_MESSAGES_FOR_MINIMAP = 8
const DESCRIPTION_PREVIEW_LENGTH = 320
const MINIMAP_AUTO_DISMISS_MS = 4500
const HOVER_Y_CACHE_THRESHOLD_PX = 2
const RESET_DISMISS_THROTTLE_MS = 400

function getTooltipLineClamp(): number {
  if (typeof window === 'undefined') return 4
  const h = window.innerHeight
  if (h < 400) return 2
  if (h < 500) return 3
  if (h < 600) return 4
  if (h < 700) return 5
  if (h < 800) return 6
  return 7
}

export interface MinimapMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content?: string
}

interface ChatMessagesMinimapProps {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>
  messages: MinimapMessage[]
  visible: boolean
  onRequestClose: () => void
  className?: string
}

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/^#+\s*/gm, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export type MinimapLabel = { role: 'user' | 'assistant' | 'system'; preview: string }

function getMessageLabel(msg: MinimapMessage): MinimapLabel {
  const raw = (msg.content || '').replace(/\s+/g, ' ').trim()
  const stripped = stripMarkdown(raw)
  const preview = stripped.slice(0, DESCRIPTION_PREVIEW_LENGTH) + (stripped.length > DESCRIPTION_PREVIEW_LENGTH ? '…' : '')
  return { role: msg.role, preview: preview || '' }
}

export function ChatMessagesMinimap({
  scrollContainerRef,
  messages,
  visible,
  onRequestClose,
  className
}: ChatMessagesMinimapProps) {
  const { t } = useTranslation()
  const [viewport, setViewport] = useState({ top: 0, height: 0 })
  const [dotRange, setDotRange] = useState<{ startPct: number; endPct: number } | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [tooltipLineClamp, setTooltipLineClamp] = useState(4)
  const minimapRef = useRef<HTMLDivElement>(null)
  const segmentsRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number>(0)
  const autoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hoveredIndexRef = useRef<number | null>(null)
  const lastViewportRef = useRef({ top: 0, height: 0 })
  const lastHoverYRef = useRef<number>(0)
  const lastHoverIndexRef = useRef<number | null>(null)
  const lastResetDismissTimeRef = useRef<number>(0)

  const count = messages.length
  const showMinimap = visible && count >= MIN_MESSAGES_FOR_MINIMAP

  const updateViewport = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el || count === 0) return
    const { scrollTop, scrollHeight, clientHeight } = el
    const total = scrollHeight - clientHeight
    if (total <= 0) {
      if (lastViewportRef.current.top !== 0 || lastViewportRef.current.height !== 100) {
        lastViewportRef.current = { top: 0, height: 100 }
        setViewport({ top: 0, height: 100 })
      }
      return
    }
    const topPct = (scrollTop / scrollHeight) * 100
    const heightPct = (clientHeight / scrollHeight) * 100
    const height = Math.min(heightPct, 100 - topPct)
    const last = lastViewportRef.current
    if (Math.abs(last.top - topPct) < 0.3 && Math.abs(last.height - height) < 0.3) return
    lastViewportRef.current = { top: topPct, height }
    setViewport({ top: topPct, height })
  }, [scrollContainerRef, count])

  useEffect(() => {
    if (!showMinimap || !segmentsRef.current) return
    let rafId = 0
    const measure = () => {
      const seg = segmentsRef.current
      if (!seg) return
      const buttons = seg.querySelectorAll<HTMLButtonElement>('button')
      if (buttons.length < 2) {
        setDotRange(null)
        return
      }
      const segRect = seg.getBoundingClientRect()
      const first = buttons[0].getBoundingClientRect()
      const last = buttons[buttons.length - 1].getBoundingClientRect()
      const segH = segRect.height
      if (segH <= 0) return
      const startPct = (first.top - segRect.top) / segH * 100
      const endPct = (last.bottom - segRect.top) / segH * 100
      setDotRange({ startPct: Math.max(0, startPct), endPct: Math.min(100, endPct) })
    }
    const scheduleMeasure = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        measure()
        rafId = 0
      })
    }
    scheduleMeasure()
    const ro = new ResizeObserver(scheduleMeasure)
    ro.observe(segmentsRef.current)
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      ro.disconnect()
    }
  }, [showMinimap, count])

  const scrollToPosition = useCallback((clickY: number) => {
    const el = scrollContainerRef.current
    const minimap = minimapRef.current
    if (!el || !minimap) return

    const minimapRect = minimap.getBoundingClientRect()
    const clickRelativeY = clickY - minimapRect.top
    const clickPercent = clickRelativeY / minimapRect.height

    const { scrollHeight, clientHeight } = el
    const maxScroll = scrollHeight - clientHeight
    const targetScroll = clickPercent * maxScroll

    el.scrollTo({
      top: Math.max(0, Math.min(targetScroll, maxScroll)),
      behavior: 'smooth'
    })
  }, [scrollContainerRef])

  const scrollToMessageAt = useCallback((index: number) => {
    const msg = messages[index]
    if (!msg) return
    const el = document.getElementById(`message-${msg.id}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      const container = scrollContainerRef.current
      if (!container || count <= 1) return
      const percent = index / (count - 1)
      const { scrollHeight, clientHeight } = container
      const maxScroll = scrollHeight - clientHeight
      container.scrollTo({
        top: Math.max(0, Math.min(percent * maxScroll, maxScroll)),
        behavior: 'smooth'
      })
    }
  }, [messages, count, scrollContainerRef])

  const getIndexFromClientY = useCallback((clientY: number) => {
    if (count === 0) return null
    const lastY = lastHoverYRef.current
    const lastIdx = lastHoverIndexRef.current
    if (lastIdx !== null && Math.abs(clientY - lastY) < HOVER_Y_CACHE_THRESHOLD_PX) return lastIdx

    const segments = segmentsRef.current
    if (!segments) return null

    const buttons = segments.querySelectorAll<HTMLButtonElement>('button')
    if (buttons.length === 0) return null

    for (let i = 0; i < buttons.length; i++) {
      const r = buttons[i].getBoundingClientRect()
      const mid = r.top + r.height / 2
      if (clientY <= mid) {
        const idx = Math.max(0, Math.min(i, count - 1))
        lastHoverYRef.current = clientY
        lastHoverIndexRef.current = idx
        return idx
      }
    }
    const idx = Math.min(buttons.length - 1, count - 1)
    lastHoverYRef.current = clientY
    lastHoverIndexRef.current = idx
    return idx
  }, [count])

  const setHoveredIndexIfChanged = useCallback((idx: number | null) => {
    if (hoveredIndexRef.current === idx) return
    hoveredIndexRef.current = idx
    setHoveredIndex(idx)
  }, [])

  useEffect(() => {
    hoveredIndexRef.current = hoveredIndex
  }, [hoveredIndex])

  useEffect(() => {
    if (!showMinimap) {
      setHoveredIndex(null)
      hoveredIndexRef.current = null
      lastHoverIndexRef.current = null
    }
  }, [showMinimap])

  useEffect(() => {
    if (!showMinimap) return
    const el = scrollContainerRef.current
    if (!el) return
    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(() => {
        updateViewport()
        rafRef.current = 0
      })
    }
    onScroll()
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', onScroll)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [showMinimap, scrollContainerRef, updateViewport])

  useEffect(() => {
    setTooltipLineClamp(getTooltipLineClamp())
    let rafId = 0
    const onResize = () => {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        setTooltipLineClamp(getTooltipLineClamp())
        rafId = 0
      })
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [])

  useEffect(() => {
    if (!showMinimap) return
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node
      if (minimapRef.current && !minimapRef.current.contains(target)) {
        onRequestClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside, { passive: true })
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [showMinimap, onRequestClose])

  useEffect(() => {
    if (!showMinimap) return
    autoDismissRef.current = setTimeout(() => {
      onRequestClose()
      autoDismissRef.current = null
    }, MINIMAP_AUTO_DISMISS_MS)
    return () => {
      if (autoDismissRef.current) {
        clearTimeout(autoDismissRef.current)
        autoDismissRef.current = null
      }
    }
  }, [showMinimap, onRequestClose])

  const resetAutoDismiss = useCallback((fromMove = false) => {
    const now = Date.now()
    if (fromMove && now - lastResetDismissTimeRef.current < RESET_DISMISS_THROTTLE_MS) return

    lastResetDismissTimeRef.current = now
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current)
      autoDismissRef.current = null
    }
    if (showMinimap) {
      autoDismissRef.current = setTimeout(() => {
        onRequestClose()
        autoDismissRef.current = null
      }, MINIMAP_AUTO_DISMISS_MS)
    }
  }, [showMinimap, onRequestClose])

  const label = useMemo(() => {
    if (hoveredIndex === null) return null
    const msg = messages[hoveredIndex]
    if (!msg) return null
    return getMessageLabel(msg)
  }, [hoveredIndex, messages])

  if (count < MIN_MESSAGES_FOR_MINIMAP) return null

  return (
    <AnimatePresence mode="wait">
      {showMinimap && (
        <motion.div
          key="minimap"
          ref={minimapRef}
          initial={{ opacity: 0, scaleX: 0.8 }}
          animate={{ opacity: 1, scaleX: 1 }}
          exit={{ opacity: 0, scaleX: 0.8 }}
          transition={{ duration: 0.22, ease: 'easeInOut' }}
        className={cn(
          'absolute right-1 top-1/2 z-30 flex h-40 w-7 -translate-y-1/2 flex-col items-stretch sm:w-2',
          'bg-background/40 rounded-full py-0.5 backdrop-blur-sm',
          'min-h-[120px] touch-manipulation',
          className
        )}
        role="navigation"
        aria-label={t('chat.minimap.aria_label')}
        onMouseEnter={() => resetAutoDismiss(false)}
        onTouchStart={() => resetAutoDismiss(false)}
        onClick={(e) => {
          e.stopPropagation()
          resetAutoDismiss(false)
          const idx = getIndexFromClientY(e.clientY)
          if (idx !== null) scrollToMessageAt(idx)
          else scrollToPosition(e.clientY)
        }}
        onMouseMove={(e) => {
          resetAutoDismiss(true)
          const idx = getIndexFromClientY(e.clientY)
          setHoveredIndexIfChanged(idx)
        }}
        onMouseLeave={() => {
          lastHoverIndexRef.current = null
          setHoveredIndex(null)
        }}
        onTouchMove={(e) => {
          const touch = e.touches[0]
          if (!touch) return
          if (minimapRef.current && !minimapRef.current.contains(e.target as Node)) return
          resetAutoDismiss(true)
          const idx = getIndexFromClientY(touch.clientY)
          setHoveredIndexIfChanged(idx)
        }}
        onTouchEnd={(e) => {
          setHoveredIndex(null)
          lastHoverIndexRef.current = null
          const touch = e.changedTouches[0]
          if (!touch) return
          const idx = getIndexFromClientY(touch.clientY)
          if (idx !== null) scrollToMessageAt(idx)
          else scrollToPosition(touch.clientY)
        }}
      >
        {/* Segments container: viewport drawn inside so bottom aligns with last dot */}
        <div ref={segmentsRef} className="relative flex flex-1 flex-col items-center gap-0.5 p-0.5">
          {/* Viewport indicator: mapped to first–last dot so bottom ends at last dot */}
          <div
            className="absolute inset-0 rounded-full"
            style={{ pointerEvents: 'none' }}
            aria-hidden
          >
            {dotRange ? (
              (() => {
                const range = dotRange.endPct - dotRange.startPct
                if (range <= 0) return null
                const topPct = dotRange.startPct + (viewport.top / 100) * range
                const bottomPctRaw = dotRange.startPct + ((viewport.top + viewport.height) / 100) * range
                const bottomPct = Math.min(bottomPctRaw, dotRange.endPct, 100)
                const topClamped = Math.max(0, Math.min(topPct, bottomPct - 2))
                const heightPct = Math.max(2, Math.min(bottomPct - topClamped, 100 - topClamped))
                if (heightPct <= 0) return null
                return (
                  <div
                    className="bg-foreground/15 absolute inset-x-0 rounded-full"
                    style={{
                      top: `${topClamped}%`,
                      height: `${heightPct}%`
                    }}
                  />
                )
              })()
            ) : (
              <div
                className="bg-foreground/15 absolute inset-x-0 rounded-full"
                style={{
                  top: `${viewport.top}%`,
                  height: `${Math.max(viewport.height, 2)}%`
                }}
              />
            )}
          </div>
          {messages.map((_, index) => (
            <button
              key={messages[index].id}
              type="button"
              aria-label={t('chat.minimap.go_to_message', { index: index + 1 })}
              className={cn(
                'size-0.5 rounded-full transition-all',
                hoveredIndex === index && 'bg-foreground/40 size-1',
                hoveredIndex !== index && 'bg-foreground/15 hover:bg-foreground/25'
              )}
              onMouseEnter={() => setHoveredIndexIfChanged(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={(e) => {
                e.stopPropagation()
                scrollToMessageAt(index)
              }}
            />
          ))}
        </div>
        {/* Tooltip: person icon for user, assistant icon for assistant; markdown stripped */}
        <AnimatePresence>
          {label && hoveredIndex !== null && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              className="border-border/30 bg-popover/95 text-popover-foreground absolute right-full mr-2 flex -translate-y-1/2 items-start gap-1.5 rounded-md border px-2 py-1.5 text-left text-xs shadow-sm backdrop-blur-sm"
              style={{
                width: 180,
                maxWidth: 'min(180px, 40vw)',
                top: `${((hoveredIndex + 0.5) / count) * 100}%`
              }}
            >
              <span className="text-muted-foreground mt-0.5 flex shrink-0 items-center justify-center" aria-hidden>
                {label.role === 'user' ? (
                  <IconUser size={14} stroke={2} />
                ) : (
                  <Image src={logo} alt="" width={14} height={14} className="rounded-sm" />
                )}
              </span>
              <p
                className="min-w-0 flex-1 break-words"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: tooltipLineClamp,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {label.preview || (label.role === 'user' ? t('chat.minimap.you') : 'Losi')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      )}
    </AnimatePresence>
  )
}
